'use client';

import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParams } from 'next/navigation';

import { cn } from '@packages/shared/src/utils/cn/cn';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { searchParamsToObject } from '@packages/shared/src/utils/searchParamsToObject/searchParamsToObject';
import { mergeQueryParameters } from '@packages/shared/src/utils/mergeQueryParameters/mergeQueryParameters';
import { Link } from '@packages/modules/src/Link/Link';
import { BypassBlock } from '@packages/shared/src/ui/BypassBlock/BypassBlock';

// caps the hover overlay's rendered size list so it never grows taller than the product tile
const MAX_VISIBLE_VARIATIONS = 18;

const messages = defineMessages({
  moreSizesAvailable: {
    id: 'availabilityOverlay.moreSizesAvailable',
    defaultMessage: 'weitere Größen verfügbar',
  },
});

export type SimpleAvailability = {
  description: string;
};

export type VariationAvailability = {
  variations: Array<{
    availability: SimpleAvailability;
    href: string;
    name: string;
  }>;
};

const ConditionalBypassBlock = ({ children, count }: { children: ReactNode; count: number }) =>
  count > 3 ? (
    <BypassBlock
      // TODO uncomment and move to proper intl messages as part of A11Y-677
      // buttonText={formatMessage(messages.bypassLabel, {
      //   count,
      // })}
      buttonText={`Skip availability information for ${count} sizes`}
      className="text-center"
    >
      {children}
    </BypassBlock>
  ) : (
    <div className="text-center">{children}</div>
  );

export type ProductCardV2AvailabilityOverlayProps = {
  availability: SimpleAvailability | VariationAvailability;
  variationGroupName?: string;
  alternateLink: string;
  /** handler triggered when the product link is clicked */
  onProductLinkClick?: () => void;
};

export const ProductCardV2AvailabilityOverlay = ({
  availability,
  variationGroupName,
  alternateLink,
  onProductLinkClick,
}: ProductCardV2AvailabilityOverlayProps) => {
  const { localizeUrl, language } = useI18n();
  const searchParams = useSearchParams();
  const { formatMessage } = useIntl();

  const buildFinalProductUrl = (productUrl: string) => {
    const finalProductUrl = mergeQueryParameters(productUrl, searchParamsToObject(searchParams));
    return localizeUrl(finalProductUrl, language);
  };

  return (
    <div
      role="group"
      // TODO uncomment and move to proper intl messages as part of A11Y-677
      // aria-label={formatMessage(messages.sectionLabel)}
      aria-label="Availability information for specific sizes"
      className={cn(
        'product-card-availability-overlay opacity-0 transition-opacity duration-300 group-hover:opacity-100 focus-within:opacity-100',
        'hidden self-end [grid-area:1/2/span_1/span_1] md:block',
        'group-hover:bg-color-grey-light/90 pointer-events-none z-20 bg-[rgba(255,255,255,0.9)] p-1', // Only show on desktop and allow interaction
      )}
    >
      <ConditionalBypassBlock
        count={
          'variations' in availability
            ? Math.min(availability.variations.length, MAX_VISIBLE_VARIATIONS)
            : 1
        }
      >
        {variationGroupName && (
          <p className="text-size-body-xs pointer-events-auto mb-1">{variationGroupName}</p>
        )}
        {'description' in availability ? (
          alternateLink && (
            <Link
              className="text-size-body-xs pointer-events-auto m-[0.3rem] inline-block cursor-pointer hover:underline"
              href={alternateLink}
              onClick={onProductLinkClick}
              enablePageTransition
            >
              {availability.description}
            </Link>
          )
        ) : (
          <>
            {availability.variations.slice(0, MAX_VISIBLE_VARIATIONS).map((variation) => (
              <Link
                key={variation.name}
                title={variation.availability.description}
                href={buildFinalProductUrl(variation.href)}
                className="text-size-body-xs pointer-events-auto m-[0.3rem] inline-block cursor-pointer hover:underline"
                onClick={onProductLinkClick}
                enablePageTransition
              >
                {availability.variations.length > 1
                  ? variation.name
                  : variation.availability.description}
              </Link>
            ))}
            {availability.variations.length > MAX_VISIBLE_VARIATIONS && (
              <p className="text-size-body-xs pointer-events-auto mt-1">
                {formatMessage(messages.moreSizesAvailable)}
              </p>
            )}
          </>
        )}
      </ConditionalBypassBlock>
    </div>
  );
};
